import Swal from 'sweetalert2';
import { MeetingService } from '../../../apps/meeting/services/MeetingService';
import { useMeetingStore } from '../meeting.store';
import { RECORDING_MAX_DURATION_MS, useRecordingStore } from '../recording.store';
import { useWorkplaceStore } from '../workplace.store';
import { Logger } from '../../../shared/services/logger.service';
import { transcriptCollectionService } from '../../../shared/services/TranscriptCollectionService';

const RECORDING_END_GRACE_PERIOD_MS = 45000;
const DEFAULT_RECORDING_TIMEOUT_DISMISS_MS = 10 * 60 * 1000;

const getRecordingTimeoutDismissMs = () => {
  const rawValue = import.meta.env.VITE_RECORDING_TIMEOUT_DISMISS_MS;
  if (!rawValue) return DEFAULT_RECORDING_TIMEOUT_DISMISS_MS;

  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    console.warn(
      `[Recording] Invalid VITE_RECORDING_TIMEOUT_DISMISS_MS: ${rawValue}. Using default.`
    );
    return DEFAULT_RECORDING_TIMEOUT_DISMISS_MS;
  }

  return parsed;
};

export const isCurrentUserInitiator = () => {
  const { activeRecordingInfo } = useRecordingStore.getState();
  const { user } = useWorkplaceStore.getState();
  return activeRecordingInfo?.initiatorUserPid === user?.pid;
};

const scheduleStopRecording = () => {
  const timeoutId = setTimeout(() => {
    const { activeRecordingInfo } = useRecordingStore.getState();
    const initiatorPid = activeRecordingInfo?.initiatorUserPid;
    const participantIds = useMeetingStore.getState().getAllParticipantIds();
    const initiatorStillAbsent = !participantIds.includes(initiatorPid);

    if (initiatorStillAbsent) {
      Logger.debug('[Recording] Timeout expired — stopping recording...');
      // Every client left in the room runs this; only the elected moderator reports it.
      const isElected =
        useRecordingStore.getState().temporaryModeratorId ===
        useWorkplaceStore.getState().user?.pid;
      MeetingService.instance.endRecordingSession(
        getDefaultRecordingName(),
        isElected ? 'initiator_absent' : null
      );
    }

    useRecordingStore.getState().setRecordingStopTimeoutId(null);
  }, RECORDING_END_GRACE_PERIOD_MS);

  useRecordingStore.getState().setRecordingStopTimeoutId(timeoutId);
};

const cancelStopRecording = () => {
  const { recordingStopTimeoutId } = useRecordingStore.getState();
  if (recordingStopTimeoutId) {
    clearTimeout(recordingStopTimeoutId);
    useRecordingStore.getState().setRecordingStopTimeoutId(null);
  }
};

const electNextModerator = (): string | null => {
  const participants = useMeetingStore.getState().jitsiParticipants;
  const userPids = Object.values(participants)
    .map((p) => p.userPid || '')
    .filter((pid) => pid !== '');

  if (userPids.length === 0) return null;

  userPids.sort((a, b) => a.localeCompare(b));
  return userPids[0];
};

const assignModeratorAndScheduleStop = () => {
  const nextMod = electNextModerator();
  cancelStopRecording();

  if (nextMod) {
    Logger.debug(`[Recording] Assigned temporary moderator: ${nextMod}`);
    useRecordingStore.getState().setTemporaryModerator(nextMod);
    scheduleStopRecording();
  } else {
    Logger.debug('[Recording] No participants left — stopping recording immediately.');
  }
};
const scheduleStopTranscription = () => {
  const timeoutId = setTimeout(() => {
    const { manualTranscriptionInitiatorPid } = useRecordingStore.getState();
    const participantIds = useMeetingStore.getState().getAllParticipantIds();
    const initiatorStillAbsent =
      !manualTranscriptionInitiatorPid || !participantIds.includes(manualTranscriptionInitiatorPid);

    if (initiatorStillAbsent) {
      console.log('[Transcription] Grace period expired — stopping manual transcription...');
      // Every client left in the room runs this; only the one a moderator would be elected
      // from reports it.
      const isElected = electNextModerator() === useWorkplaceStore.getState().user?.pid;
      MeetingService.instance.stopManualTranscription(true, isElected ? 'initiator_absent' : null);
    }

    useRecordingStore.getState().setTranscriptionStopTimeoutId(null);
  }, RECORDING_END_GRACE_PERIOD_MS);

  useRecordingStore.getState().setTranscriptionStopTimeoutId(timeoutId);
};

const cancelStopTranscription = () => {
  const { transcriptionStopTimeoutId } = useRecordingStore.getState();
  if (transcriptionStopTimeoutId) {
    clearTimeout(transcriptionStopTimeoutId);
    useRecordingStore.getState().setTranscriptionStopTimeoutId(null);
  }
};

export const getDefaultRecordingName = (name?: string) => {
  const { currentRoom } = useWorkplaceStore.getState();
  const title = (currentRoom?.name || 'Untitled').replace(/\s+/g, '_');
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, '_');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const safeName = (name || 'Unknown').replace(/\s+/g, '_');
  return `${title}_${date}_${hours}_${minutes}_${safeName}.mp4`;
};

/**
 * Validates and handles active recording or standalone transcription before exit actions (logout, end call).
 * Shows popup prompts and handles cleanup.
 * Returns true if the exit action should proceed, false if it should be cancelled.
 */
export const handleRecordingValidationBeforeExit = async (): Promise<boolean> => {
  const {
    roomRecordingStatus,
    activeRecordingInfo,
    isManualTranscriptionActive,
    manualTranscriptionInitiatorPid,
  } = useRecordingStore.getState();
  const { user } = useWorkplaceStore.getState();
  const isRecordingActive = roomRecordingStatus === 'on';
  const isRecordingInitiator = activeRecordingInfo?.initiatorUserPid === user?.pid;
  const isManualTranscriptionInitiator = manualTranscriptionInitiatorPid === user?.pid;

  // If recording active and user is initiator, show popup
  if (isRecordingActive && isRecordingInitiator) {
    const result = await MeetingService.instance.showLeaveRoomAlert();

    if (result === 'continue') {
      // User wants to continue recording, cancel exit
      return false;
    }

    // User chose to save and exit - prompt for recording title
    const title = await MeetingService.instance.showRecordingTitlePrompt();

    if (!title) {
      // User cancelled the title prompt, cancel exit
      return false;
    }

    // Show progress while saving
    const hasTranscription = transcriptCollectionService.isCollecting();

    Swal.fire({
      title: 'Saving Recording...',
      html: hasTranscription
        ? 'Please wait while we save your recording and finalize the transcript. This may take a moment.'
        : 'Please wait while we save your recording.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await MeetingService.instance.endRecordingSession(title, 'left_room');
    } catch (error) {
      console.error('[Recording] Error during recording cleanup:', error);
      // Continue with exit even if there's an error
    } finally {
      // Always close the loading dialog
      Swal.close();
    }

    return true;
  }
  // If standalone transcription active and user is initiator, show popup
  else if (isManualTranscriptionActive && isManualTranscriptionInitiator) {
    const result = await MeetingService.instance.showLeaveRoomAlertForCaptions();

    if (result === 'continue') {
      // User wants to continue captions, cancel exit
      return false;
    }

    // User chose to save and exit - show progress while saving
    Swal.fire({
      title: 'Saving Captions...',
      html: 'Please wait while we finalize the transcript.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      await MeetingService.instance.stopManualTranscription(false, 'left_room');
    } catch (error) {
      console.error('[Recording] Error during transcription cleanup:', error);
      // Continue with exit even if there's an error
    } finally {
      // Always close the loading dialog
      Swal.close();
    }

    return true;
  }

  // If user is a non-initiator but there's active transcription/recording, clear their local state
  if (isManualTranscriptionActive || activeRecordingInfo) {
    Logger.debug('[handleRecordingValidationBeforeExit] Clearing state for non-initiator on exit');
    useRecordingStore.getState().clearRecordingSession();
  }

  // No active recording or transcription (or we just cleared it), proceed with exit
  return true;
};

/**
 * Checks for an active recording or standalone transcription before room navigation and shows a leave-room alert if needed.
 * Returns true if navigation should proceed, false if it should be cancelled.
 */
export const handleRecordingGuardBeforeRoomChange = async (
  targetRoomPid: string
): Promise<boolean> => {
  const { activeRecordingInfo, isManualTranscriptionActive, manualTranscriptionInitiatorPid } =
    useRecordingStore.getState();
  const { user } = useWorkplaceStore.getState();

  if (!activeRecordingInfo) return true;

  const { recordingRoomPid, initiatorUserPid } = activeRecordingInfo;
  const isRecordingActive = useRecordingStore.getState().roomRecordingStatus === 'on';
  const isRecordingInitiator = initiatorUserPid === user?.pid;
  const isManualTranscriptionInitiator = manualTranscriptionInitiatorPid === user?.pid;
  const isLeavingRecordingRoom = recordingRoomPid !== targetRoomPid;

  // Check if initiator is leaving a room with active recording or standalone transcription
  if (isLeavingRecordingRoom) {
    // Handle recording initiator
    if (isRecordingActive && isRecordingInitiator) {
      const result = await MeetingService.instance.showLeaveRoomAlert();
      if (result === 'continue') return false;

      // Prompt for recording title
      const title = await MeetingService.instance.showRecordingTitlePrompt();
      if (!title) return false; // User cancelled, don't leave room

      await MeetingService.instance.endRecordingSession(title, 'left_room');
      useRecordingStore.getState().clearRecordingSession();
    }
    // Handle standalone transcription initiator
    else if (isManualTranscriptionActive && isManualTranscriptionInitiator) {
      const result = await MeetingService.instance.showLeaveRoomAlertForCaptions();
      if (result === 'continue') return false;
      await MeetingService.instance.stopManualTranscription(false, 'left_room');
    }
  }

  // Clear state for non-initiators when leaving the recording/transcription room
  if (isLeavingRecordingRoom) {
    if (user?.pid !== initiatorUserPid) {
      useRecordingStore.getState().clearRecordingSession();
    }
    // Clear manual transcription state for non-initiators
    if (isManualTranscriptionActive && user?.pid !== manualTranscriptionInitiatorPid) {
      useRecordingStore.getState().setManualTranscriptionActive(false);
    }
  }

  return true;
};

export const reassignRecordingModerator = (participantUserPid: string) => {
  const { temporaryModeratorId, roomRecordingStatus, activeRecordingInfo } =
    useRecordingStore.getState();

  if (roomRecordingStatus !== 'on') return;

  const initiatorPid = activeRecordingInfo?.initiatorUserPid;
  Logger.debug(`[Recording] Participant leaving: ${participantUserPid}`);
  if (!participantUserPid) return;

  const isInitiatorLeaving = participantUserPid === initiatorPid;
  const isTempModeratorLeaving = participantUserPid === temporaryModeratorId;

  if (isInitiatorLeaving || isTempModeratorLeaving) {
    assignModeratorAndScheduleStop();
  }
};

export const restoreRecordingModerator = (participantUserPid: string) => {
  const { activeRecordingInfo, roomRecordingStatus, recordingStopTimeoutId } =
    useRecordingStore.getState();

  if (roomRecordingStatus !== 'on') return;

  const initiatorPid = activeRecordingInfo?.initiatorUserPid;

  if (participantUserPid === initiatorPid && recordingStopTimeoutId) {
    Logger.debug('[Recording] Initiator rejoined — canceling stop timer');
    clearTimeout(recordingStopTimeoutId);
    useRecordingStore.getState().setRecordingStopTimeoutId(null);
    useRecordingStore.getState().setTemporaryModerator(null);
  }
};

export const reassignTranscriptionModerator = (participantUserPid: string) => {
  const { isManualTranscriptionActive, manualTranscriptionInitiatorPid } =
    useRecordingStore.getState();

  if (!isManualTranscriptionActive) return;

  const initiatorPid = manualTranscriptionInitiatorPid;
  console.log(`[Transcription] Participant leaving: ${participantUserPid}`);
  if (!participantUserPid) return;

  const isInitiatorLeaving = participantUserPid === initiatorPid;

  if (isInitiatorLeaving) {
    console.log('[Transcription] Initiator left — scheduling transcription stop');
    cancelStopTranscription();
    scheduleStopTranscription();
  }
};

export const restoreTranscriptionModerator = (participantUserPid: string) => {
  const {
    isManualTranscriptionActive,
    manualTranscriptionInitiatorPid,
    transcriptionStopTimeoutId,
  } = useRecordingStore.getState();

  if (!isManualTranscriptionActive) return;

  const initiatorPid = manualTranscriptionInitiatorPid;

  if (participantUserPid === initiatorPid && transcriptionStopTimeoutId) {
    console.log('[Transcription] Initiator rejoined — canceling stop timer');
    clearTimeout(transcriptionStopTimeoutId);
    useRecordingStore.getState().setTranscriptionStopTimeoutId(null);
  }
};

export const showRecordingTimeoutAlert = async (): Promise<'continue' | 'end'> => {
  const result = await Swal.fire({
    title: 'Recording Duration Limit',
    text: 'You have been recording for 2 hours. Would you like to continue recording or end it now?',
    icon: 'info',
    showCancelButton: true,
    confirmButtonText: 'Continue Recording',
    cancelButtonText: 'End Recording',
    reverseButtons: true,
    allowOutsideClick: false,
    timer: getRecordingTimeoutDismissMs(),
    timerProgressBar: true,
  });

  if (result.dismiss === Swal.DismissReason.timer) {
    Logger.debug('[Recording] ⏰ No response after 5 minutes — auto ending recording.');
    return 'end';
  }

  return result.isConfirmed ? 'continue' : 'end';
};

export const startRecordingDurationWatcher = () => {
  if (!isCurrentUserInitiator()) return;

  const store = useRecordingStore.getState();

  if (store.recordingDurationWatcherId) {
    clearTimeout(store.recordingDurationWatcherId);
    store.setRecordingDurationWatcherId(null);
  }

  const startTime = store.activeRecordingInfo?.recordingStartTime
    ? new Date(store.activeRecordingInfo.recordingStartTime).getTime()
    : Date.now();

  const elapsed = Date.now() - startTime;
  const remaining = Math.max(RECORDING_MAX_DURATION_MS - elapsed, 0);

  if (remaining <= 0) {
    console.warn('[Recording] ⚠️ Max duration already exceeded — skipping watcher setup.');
    return;
  }

  Logger.debug(
    `[Recording] ⏳ Starting duration watcher — remaining: ${(remaining / 60000).toFixed(1)} minutes`
  );
  const timeoutId = setTimeout(async () => {
    const choice = await showRecordingTimeoutAlert();

    if (choice === 'end') {
      Logger.debug('[Recording] 🟥 Recording ended after 2 hours.');
      // Prompt for recording title before ending
      await MeetingService.instance.endRecordingSessionWithPrompt('time_limit');
    }

    useRecordingStore.getState().setRecordingDurationWatcherId(null);
  }, remaining);

  store.setRecordingDurationWatcherId(timeoutId);
};

export const clearRecordingDurationWatcher = () => {
  if (!isCurrentUserInitiator()) return;

  const { recordingDurationWatcherId, setRecordingDurationWatcherId } =
    useRecordingStore.getState();

  if (recordingDurationWatcherId) {
    clearTimeout(recordingDurationWatcherId);
    setRecordingDurationWatcherId(null);
    Logger.debug('[Recording] 🧹 Cleared recording duration watcher.');
  }
};
